import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CalendarDays,
  Car,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Download,
  FerrisWheel,
  Landmark,
  MapPin,
  MessageSquareText,
  PhoneCall,
  Telescope,
} from "lucide-react";
import {
  formatPackagePrice,
} from "../Packages/packages";
import {
  getBookingPlaceSlug,
} from "./Booking.js";
import "./Booking.css";


export default function Booking() {
    const navigate = useNavigate();
  const location = useLocation();
  const { place = "", packageSlug = "" } = useParams();

  const placeSlug = getBookingPlaceSlug(place);

  const API_BASE =
  (
    typeof process !== "undefined" && process.env?.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL
      : "http://localhost:5000"
  ).replace(/\/$/, "");

  const statePackage = location.state?.package || null;

  const [pkg, setPkg] = useState(null);
  const [packageLoading, setPackageLoading] = useState(true);
  const [packageError, setPackageError] = useState("");

  const destination =
  pkg?.place ||
  pkg?.location ||
  placeSlug
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  const fromCity = location.state?.from || "New Delhi";

  useEffect(() => {
    let cancelled = false;

    const loadAuthoritativePackage = async () => {
      setPackageLoading(true);
      setPackageError("");
      setPkg(null);

      try {
        let packageData = null;

        /*
         * First choice:
         * If Packages.jsx passed the real database package ID,
         * fetch that exact package from the backend.
         */
        if (statePackage?.id) {
          const response = await fetch(
            `${API_BASE}/api/packages/${encodeURIComponent(statePackage.id)}`
          );

          if (response.status === 404) {
            throw new Error("Package is no longer available.");
          }

          if (!response.ok) {
            throw new Error("Unable to load package.");
          }

          const data = await response.json();
          packageData = data?.data || data;
        } else {
          /*
           * Direct URL entry:
           * use the existing public package endpoint and find the
           * exact package matching the URL.
           */
          if (!placeSlug || !packageSlug) {
            throw new Error("Invalid package URL.");
          }

          const response = await fetch(
            `${API_BASE}/api/packages?place=${encodeURIComponent(placeSlug)}`
          );

          if (!response.ok) {
            throw new Error("Unable to load packages.");
          }

          const data = await response.json();
          const packages = Array.isArray(data)
            ? data
            : Array.isArray(data?.data)
              ? data.data
              : [];

          const normalizeSlug = (value = "") =>
            String(value)
              .trim()
              .toLowerCase()
              .replace(/&/g, "and")
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-+|-+$/g, "");

          packageData =
            packages.find(
              (item) => normalizeSlug(item.title) === normalizeSlug(packageSlug)
            ) || null;

          if (!packageData) {
            throw new Error("Package not found.");
          }

          /*
           * Re-fetch the exact package by ID so the booking page
           * always uses the authoritative database record.
           */
          if (packageData.id) {
            const exactResponse = await fetch(
              `${API_BASE}/api/packages/${encodeURIComponent(packageData.id)}`
            );

            if (exactResponse.status === 404) {
              throw new Error("Package is no longer available.");
            }

            if (!exactResponse.ok) {
              throw new Error("Unable to load package.");
            }

            const exactData = await exactResponse.json();
            packageData = exactData?.data || exactData;
          }
        }

        if (!packageData?.id) {
          throw new Error("Invalid package returned by server.");
        }

        if (!cancelled) {
          setPkg({
            ...packageData,
            image: packageData.image_url || packageData.image || "",
            place:
              packageData.place ||
              packageData.location ||
              placeSlug,
          });
        }
      } catch (error) {
        if (!cancelled) {
          setPackageError(error.message || "Unable to load package.");
          setPkg(null);
        }
      } finally {
        if (!cancelled) {
          setPackageLoading(false);
        }
      }
    };

    loadAuthoritativePackage();

    return () => {
      cancelled = true;
    };
  }, [API_BASE, packageSlug, placeSlug, statePackage?.id]);

  const parsePackageArray = (value) => {
  if (Array.isArray(value)) return value;

  if (!value) return [];

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  return [];
};

const places = useMemo(() => {
  if (!pkg?.location) return [];

  return String(pkg.location)
    .split("|")
    .map((item) => item.trim())
    .filter(Boolean);
}, [pkg?.location]);

const itinerary = useMemo(
  () => parsePackageArray(pkg?.itinerary),
  [pkg?.itinerary]
);

const inclusions = useMemo(
  () => parsePackageArray(pkg?.inclusions),
  [pkg?.inclusions]
);

const exclusions = useMemo(
  () => parsePackageArray(pkg?.exclusions),
  [pkg?.exclusions]
);

const highlights = useMemo(
  () => parsePackageArray(pkg?.highlights),
  [pkg?.highlights]
);

const facilities = useMemo(
  () => parsePackageArray(pkg?.facilities),
  [pkg?.facilities]
);

const departureDates = useMemo(
  () => parsePackageArray(pkg?.departure_dates),
  [pkg?.departure_dates]
);

/* Reference-feature data loaded by GET /api/packages/:id. */
const routeMap = useMemo(() => parsePackageArray(pkg?.route_map), [pkg?.route_map]);
const dayActivities = useMemo(() => parsePackageArray(pkg?.day_activities), [pkg?.day_activities]);
const dayHotels = useMemo(() => parsePackageArray(pkg?.day_hotels), [pkg?.day_hotels]);
const dayTransfers = useMemo(() => parsePackageArray(pkg?.day_transfers), [pkg?.day_transfers]);
const faqs = useMemo(() => parsePackageArray(pkg?.faqs), [pkg?.faqs]);
const policies = useMemo(() => parsePackageArray(pkg?.policies), [pkg?.policies]);
const inclusionLibrary = useMemo(() => parsePackageArray(pkg?.inclusion_options), [pkg?.inclusion_options]);
const exclusionLibrary = useMemo(() => parsePackageArray(pkg?.exclusion_options), [pkg?.exclusion_options]);
const pricePeriods = useMemo(() => parsePackageArray(pkg?.price_periods), [pkg?.price_periods]);

/*
 * Uploaded images are stored as "/uploads/…" (backend-relative) while
 * external images are full URLs — resolve both against the API origin.
 */
const mediaUrl = (value) => {
  const url = String(value || "").trim();
  if (!url || /^https?:\/\//i.test(url)) return url;
  return `${API_BASE}${url.startsWith("/") ? "" : "/"}${url}`;
};

/* Per-day extras come back as flat { day, name } rows. */
const extrasForDay = (rows, day) =>
  rows.filter((row) => Number(row.day) === Number(day)).map((row) => row.name);

/* Prices are displayed in the package's saved currency. */
const money = (value) => formatPackagePrice(value, pkg?.currency || "INR");

/*
 * Customer gallery = main package image + any tour-gallery images saved by
 * the admin. Falls back to the main image alone when no gallery exists.
 */
const galleryImages = useMemo(() => {
  const images = [];

  if (pkg?.image) {
    images.push({ image_url: mediaUrl(pkg.image), alt_text: pkg.title || "Holiday package" });
  }

  const extras = parsePackageArray(pkg?.gallery)
    .map(item => (typeof item === "string" ? { image_url: item, alt_text: "" } : item))
    .filter(item => item?.image_url)
    .map(item => ({ ...item, image_url: mediaUrl(item.image_url) }));

  return [...images, ...extras];
}, [pkg?.image, pkg?.gallery, pkg?.title]);

const [galleryIndex, setGalleryIndex] = useState(0);

useEffect(() => {
  setGalleryIndex(0);
}, [pkg?.id]);

const [activeTab, setActiveTab] = useState("overview");

  const [showMoreHotels, setShowMoreHotels] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedTravelDate, setSelectedTravelDate] = useState(location.state?.travelDate || "");
  const [travelDateError, setTravelDateError] = useState("");

  const formatDate = (value) => {
  if (!value) return "—";

  const datePart = String(value).slice(0, 10);
  const [year, month, day] = datePart.split("-");

  if (!year || !month || !day) return "—";

  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day)
  ).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
  
  const getToday = () => new Date().toISOString().split("T")[0];

const isValidDepartureDate = (dateValue) => {
  if (!dateValue || !pkg) return false;

  const date = new Date(`${dateValue}T00:00:00`);
  const today = new Date(`${getToday()}T00:00:00`);

  // Booking window
  if (pkg.booking_start_date && dateValue < pkg.booking_start_date) {
    return false;
  }

  if (pkg.booking_end_date && dateValue > pkg.booking_end_date) {
    return false;
  }

  // Minimum advance booking
  const minAdvance = Number(pkg.booking_min_advance_days || 0);
  const minimumDate = new Date(today);
  minimumDate.setDate(minimumDate.getDate() + minAdvance);

  if (date < minimumDate) {
    return false;
  }

  // Maximum advance booking
  const maxYears = Number(pkg.booking_max_advance_years || 0);

  if (maxYears > 0) {
    const maximumDate = new Date(today);
    maximumDate.setFullYear(maximumDate.getFullYear() + maxYears);

    if (date > maximumDate) {
      return false;
    }
  }

  // Daily
  if (pkg.departure_type === "daily") {
    return true;
  }

  // Recurring
  if (pkg.departure_type === "recurring") {
    if (!pkg.departure_start_date) return false;

    const startDate = new Date(`${pkg.departure_start_date}T00:00:00`);
    const interval = Number(pkg.departure_interval_days || 0);

    if (interval < 1 || date < startDate) return false;

    const diffDays = Math.round(
      (date - startDate) / (1000 * 60 * 60 * 24)
    );

    return diffDays % interval === 0;
  }

  // Fixed dates
  if (pkg.departure_type === "fixed") {
  return departureDates.includes(dateValue);
}

  return false;
};

  const scrollToSection = (id) => {
    setActiveTab(id);
    requestAnimationFrame(() => {
      document.getElementById(`holiday-${id}`)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const handleDownloadPdf = async () => {
  if (!pkg?.id) {
    alert("Package information is unavailable.");
    return;
  }

  try {
    const response = await fetch(
      `${API_BASE}/api/packages/${encodeURIComponent(pkg.id)}/pdf`
    );

    if (!response.ok) {
      throw new Error(`PDF generation failed: ${response.status}`);
    }

    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `${String(pkg.title || "holiday-package")
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase()}.pdf`;

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Holiday package PDF download failed:", error);
    alert("Unable to download the PDF. Please try again.");
  }
};

    if (packageLoading) {
    return (
      <main className="holiday-booking-page">
        <section className="holiday-booking-empty">
          <h1>Loading package...</h1>
          <p>Please wait while we load the latest package details.</p>
        </section>
      </main>
    );
  }

  if (packageError || !pkg) {
    return (
      <main className="holiday-booking-page">
        <section className="holiday-booking-empty">
          <h1>Package not available</h1>
          <p>{packageError || "This package could not be found."}</p>

          <button
            type="button"
            onClick={() =>
              navigate(
                placeSlug
                  ? `/holidays/packages/${placeSlug}`
                  : "/holidays"
              )
            }
          >
            Back to Packages
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="holiday-booking-page">
      <section className="holiday-booking-shell">
        <div className="holiday-booking-breadcrumb">
          <button type="button" onClick={() => navigate("/holidays")}>Home</button>
          <span>&gt;</span>
          <button type="button" onClick={() => navigate(`/holidays/packages/${placeSlug}`)}>Holidays</button>
          <span>&gt;</span>
          <strong>{pkg.title}</strong>
        </div>

        <div className="holiday-booking-head">
          <div>
            <h1>{pkg.title}</h1>

<div className="holiday-booking-meta">
  {pkg.duration && (
    <strong>{pkg.duration.replace("/", " / ")}</strong>
  )}

  {pkg.group_size && (
    <span>Up to {pkg.group_size} guests per booking</span>
  )}

  {(pkg.category_name || pkg.category_label) && (
    <span>{pkg.category_name || pkg.category_label}</span>
  )}
</div>

            <p>{places.join(" | ") || `${fromCity} | ${destination}`}</p>
            {(pkg.rating !== null &&
  pkg.rating !== undefined &&
  pkg.rating !== "") && (
  <div className="holiday-package-rating">
    <strong>★ {Number(pkg.rating).toFixed(1)}</strong>
  </div>
)}
          </div>
          <button
  className="holiday-booking-pdf"
  type="button"
  onClick={handleDownloadPdf}
>
  <Download size={16} />
  Download PDF
</button>
        </div>

        <div className="holiday-booking-grid">
          <section className="holiday-booking-main">
            <div className="holiday-booking-gallery">
              {galleryImages.length ? (
  <img
    src={galleryImages[galleryIndex]?.image_url}
    alt={galleryImages[galleryIndex]?.alt_text || pkg.title}
  />
) : (
  <div className="holiday-booking-image-placeholder">
    Package image unavailable
  </div>
)}
              {galleryImages.length > 1 && (
                <>
                  <button
                    className="holiday-gallery-arrow holiday-gallery-arrow--left"
                    type="button"
                    aria-label="Previous image"
                    onClick={() =>
                      setGalleryIndex(index =>
                        index === 0 ? galleryImages.length - 1 : index - 1
                      )
                    }
                  >
                    <ChevronLeft size={22} />
                  </button>

                  <button
                    className="holiday-gallery-arrow holiday-gallery-arrow--right"
                    type="button"
                    aria-label="Next image"
                    onClick={() =>
                      setGalleryIndex(index =>
                        index === galleryImages.length - 1 ? 0 : index + 1
                      )
                    }
                  >
                    <ChevronRight size={22} />
                  </button>

                  <span className="holiday-gallery-count" aria-live="polite">
                    {galleryIndex + 1} / {galleryImages.length}
                  </span>
                </>
              )}
            </div>

            <div className="holiday-detail-tabs" aria-label="Package details">
              {[
  ["overview", "Overview"],
  ["itinerary", "Day wise Itinerary"],
  ["inclusion", "Inclusion/Exclusions"],
  ["seasonal", "Seasonal Pricing"],
  ["policy", "Policies & FAQ"],
].map(([id, label]) => (
                <button
                  type="button"
                  key={id}
                  className={activeTab === id ? "is-active" : ""}
                  onClick={() => scrollToSection(id)}
                >
                  {label}
                </button>
              ))}
            </div>

            <section id="holiday-overview" className="holiday-scroll-section">
  <div className="holiday-tab-panel">
    <h2>Package Overview</h2>

    {pkg.description && (
      <div
        className="holiday-rich-text"
        dangerouslySetInnerHTML={{ __html: pkg.description }}
      />
    )}

    {highlights.length > 0 && (
      <div className="holiday-highlights-section">
        <h3>Highlights</h3>

        <ul>
          {highlights.map((item, index) => (
            <li key={`highlight-${index}`}>
              {typeof item === "object"
                ? item.name || item.title || item.description || ""
                : item}
            </li>
          ))}
        </ul>
      </div>
    )}

    {!pkg.description && highlights.length === 0 && routeMap.length === 0 && (
      <p>
        Package details are available as provided by the administrator.
      </p>
    )}

    {routeMap.length > 0 && (
      <div className="holiday-route-map">
        <h3>Route Map</h3>
        <div style={{ overflowX: "auto" }}>
          <table className="holiday-route-table">
            <thead>
              <tr>
                <th>City</th>
                <th>State</th>
                <th>Country</th>
                <th>No. of Days</th>
              </tr>
            </thead>
            <tbody>
              {routeMap.map((row, index) => (
                <tr key={`${row.city_name || row.city}-${index}`}>
                  <td>{row.city_name || row.city || "—"}</td>
                  <td>{row.state_name || row.state || "—"}</td>
                  <td>{row.country_name || row.country || "—"}</td>
                  <td>{Number(row.no_of_days) || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pkg.route_map_url && (
          <img
            className="holiday-route-image"
            src={mediaUrl(pkg.route_map_url)}
            alt="Package route map"
            onError={(event) => { event.currentTarget.style.display = "none"; }}
          />
        )}
      </div>
    )}
  </div>
</section>

            <section id="holiday-itinerary" className="holiday-scroll-section">
              <div className="holiday-tab-panel holiday-day-panel">
                <h2>Day wise Itinerary</h2>
                <div className="holiday-day-timeline">
                  {itinerary.map((item) => (
                    <div className="holiday-day-row" key={`${item.day}-tab-${item.title}`}>
                      <span className="holiday-day-badge">
  {String(item.day ?? "").replace(" ", "\n")}
</span>
                      <span className="holiday-day-dot" />
                      <article>
  <h3>{item.title || `Day ${item.day}`}</h3>

  {item.city && (
    <p className="holiday-day-city">
      <MapPin size={13} /> {item.city}
    </p>
  )}

  {item.image_url && (
    <img
      className="holiday-day-image"
      src={mediaUrl(item.image_url)}
      alt={item.title || `Day ${item.day}`}
      onError={(event) => { event.currentTarget.style.display = "none"; }}
    />
  )}

  {item.description && (
    /<[a-z/][^>]*>/i.test(String(item.description)) ? (
      <div
        className="holiday-rich-text"
        dangerouslySetInnerHTML={{ __html: item.description }}
      />
    ) : (
      <p>{item.description}</p>
    )
  )}

  {[
    ["Activities", extrasForDay(dayActivities, item.day)],
    ["Hotels", extrasForDay(dayHotels, item.day)],
    ["Transfers", extrasForDay(dayTransfers, item.day)],
  ].map(([label, values]) =>
    values.length ? (
      <div className="holiday-day-extras" key={label}>
        <strong>{label}:</strong>
        <span>
          {values.join(", ")}
        </span>
      </div>
    ) : null
  )}
</article>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section id="holiday-inclusion" className="holiday-scroll-section">
              <div className="holiday-inclusion-wrap">
                <div className="holiday-inc-exc-grid">
  <section className="holiday-inc-card holiday-inc-card--green">
    <h2>Inclusions</h2>
    {pkg.inclusion_display_text && (
      <div
        className="holiday-rich-text"
        dangerouslySetInnerHTML={{ __html: pkg.inclusion_display_text }}
      />
    )}
    <ul>
      {inclusions.map((item, index) => (
  <li key={`${String(item)}-${index}`}>
    {typeof item === "object" ? item.name || item.title || "" : item}
  </li>
))}
      {inclusionLibrary.map((option) => (
        <li key={`lib-inc-${option.id}`} title={String(option.content || "").replace(/<[^>]*>/g, " ")}>
          {option.name}
        </li>
      ))}
    </ul>
  </section>

  {facilities.length > 0 && (
  <section className="holiday-scroll-section">
    <div className="holiday-tab-panel">
      <h2>Facilities & Amenities</h2>

      <ul>
        {facilities.map((item, index) => (
          <li key={`facility-${index}`}>
            {typeof item === "object"
              ? item.name || item.title || item.description || ""
              : item}
          </li>
        ))}
      </ul>
    </div>
  </section>
)}

<div className="holiday-availability-card">
  <h3>Booking & Departure Information</h3>

  {pkg.booking_start_date && (
    <p>
      <strong>Booking Starts:</strong>{" "}
      {formatDate(pkg.booking_start_date)}
    </p>
  )}

  {pkg.booking_end_date && (
    <p>
      <strong>Booking Ends:</strong>{" "}
      {formatDate(pkg.booking_end_date)}
    </p>
  )}

  {pkg.departure_type && (
  <p>
    <strong>Departure:</strong>{" "}
    {pkg.departure_type === "recurring"
      ? `Every ${pkg.departure_interval_days} days`
      : pkg.departure_type === "fixed"
        ? "Fixed Dates"
        : "Daily"}
  </p>
)}

  {pkg.departure_type === "recurring" &&
    pkg.departure_start_date && (
      <p>
        <strong>First Departure:</strong>{" "}
        {pkg.departure_start_date}
      </p>
    )}

  {pkg.departure_type === "fixed" &&
    departureDates.length > 0 && (
      <div>
        <strong>Departure Dates:</strong>

        <ul>
          {departureDates.map((date, index) => (
            <li key={`${date}-${index}`}>
              {date}
            </li>
          ))}
        </ul>
      </div>
    )}
</div>

  <section className="holiday-inc-card holiday-inc-card--red">
    <h2>Exclusions</h2>
    {pkg.exclusion_display_text && (
      <div
        className="holiday-rich-text"
        dangerouslySetInnerHTML={{ __html: pkg.exclusion_display_text }}
      />
    )}
    <ul>
      {exclusions.map((item, index) => (
  <li key={`${String(item)}-${index}`}>
    {typeof item === "object" ? item.name || item.title || "" : item}
  </li>
))}
      {exclusionLibrary.map((option) => (
        <li key={`lib-exc-${option.id}`} title={String(option.content || "").replace(/<[^>]*>/g, " ")}>
          {option.name}
        </li>
      ))}
    </ul>
  </section>
</div>
            
              </div>
            </section>

            {pricePeriods.length > 0 && (
              <section id="holiday-seasonal" className="holiday-scroll-section">
                <div className="holiday-tab-panel">
                  <h2>Seasonal Pricing</h2>
                  <p className="holiday-seasonal-note">
                    Travel-window rates by hotel type and occupancy, in {pkg.currency || "INR"}. The final amount is confirmed at booking.
                  </p>

                  {pricePeriods.map((period, index) => (
                    <div className="holiday-period-card" key={`${period.from_date}-${period.to_date}-${index}`}>
                      <h3>
                        {formatDate(period.from_date)} — {formatDate(period.to_date)}
                      </h3>
                      <div style={{ overflowX: "auto" }}>
                        <table className="holiday-price-grid">
                          <thead>
                            <tr>
                              <th>Hotel Type</th>
                              <th>1 Pax</th>
                              <th>2 Pax</th>
                              <th>4 Pax</th>
                              <th>6 Pax</th>
                              <th>8 Pax</th>
                              <th>Extra Adult</th>
                              <th>Extra Child</th>
                              <th>Extra Infant</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(Array.isArray(period.rows) ? period.rows : []).map((row, rowIndex) => (
                              <tr key={`${row.hotel_type}-${rowIndex}`}>
                                <td>{row.hotel_type}</td>
                                {["pax_1", "pax_2", "pax_4", "pax_6", "pax_8", "extra_adult", "extra_child", "extra_infant"].map((key) => (
                                  <td key={key}>{money(Number(row[key]) || 0)}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {(policies.length > 0 || faqs.length > 0) && (
              <section id="holiday-policy" className="holiday-scroll-section">
                <div className="holiday-tab-panel">
                  <h2>Policies & FAQ</h2>

                  {policies.length > 0 && (
                    <div className="holiday-policy-list">
                      {policies.map((policy) => (
                        <details className="holiday-policy-item" key={`policy-${policy.id}`}>
                          <summary>{policy.name}</summary>
                          <div
                            className="holiday-rich-text"
                            dangerouslySetInnerHTML={{ __html: policy.content || "Content to be confirmed at booking." }}
                          />
                        </details>
                      ))}
                    </div>
                  )}

                  {faqs.length > 0 && (
                    <div className="holiday-policy-list">
                      {faqs.map((faq, index) => (
                        <details className="holiday-policy-item" key={`faq-${faq.id || index}`}>
                          <summary>{faq.question}</summary>
                          <div
                            className="holiday-rich-text"
                            dangerouslySetInnerHTML={{ __html: faq.answer || "" }}
                          />
                        </details>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            )}

          </section>

          <aside className="holiday-booking-side">
            <div className="holiday-price-card">
              <div className="holiday-price-top">
  <span>Package Price</span>

  {Number(pkg.base_price) > Number(pkg.selling_price) && (
    <del>{money(pkg.base_price)}</del>
  )}

  <strong>
    {money(Number(pkg.selling_price) || 0)}

    <small>
      {pkg.pricing_basis === "per_room"
        ? " Per Room"
        : pkg.pricing_basis === "per_booking"
          ? " Per Booking"
          : " Per Person"}
    </small>
  </strong>

  {Number(pkg.discount_percent) > 0 && (
    <span>
      {Number(pkg.discount_percent)}% OFF
    </span>
  )}
</div>

<div className="holiday-price-breakdown">
  <div>
    <span>Base Price</span>
    <strong>{money(Number(pkg.base_price) || 0)}</strong>
  </div>

  {Number(pkg.discount_amount) > 0 && (
    <div>
      <span>Discount</span>
      <strong>
        - {money(Number(pkg.discount_amount))}
      </strong>
    </div>
  )}

  {Number(pkg.gst) > 0 && (
    <div>
      <span>GST</span>
      <strong>{money(Number(pkg.gst))}</strong>
    </div>
  )}

  {Number(pkg.other_tax) > 0 && (
    <div>
      <span>Other Taxes</span>
      <strong>{money(Number(pkg.other_tax))}</strong>
    </div>
  )}

  {Number(pkg.service_fee) > 0 && (
    <div>
      <span>Service Fee</span>
      <strong>{money(Number(pkg.service_fee))}</strong>
    </div>
  )}

  {Number(pkg.other_charges) > 0 && (
    <div>
      <span>Other Charges</span>
      <strong>{money(Number(pkg.other_charges))}</strong>
    </div>
  )}

  <div>
    <span>Total Price</span>
    <strong>{money(Number(pkg.final_price) || 0)}</strong>
  </div>
</div>
              <div className="holiday-price-actions">
                <button className="holiday-price-book" type="button" onClick={() => setShowCalendar(true)}>Book Now</button>       
              </div>
            </div>

            <div className="holiday-info-card">
              <p>
                <Clock3 size={17} />
                <span><strong>Duration :</strong> {pkg.duration.replace("/", " / ")}</span>
              </p>
              <p>
                <MapPin size={17} />
                <span><strong>Places to Visit :</strong> {places.join(" | ") || destination}</span>
              </p>
              {inclusions.length > 0 && (
  <>
    <div className="holiday-includes-pill">Package Includes</div>

    <div className="holiday-includes">
      {inclusions.map((item, index) => (
        <span key={`include-${index}`}>
          {typeof item === "object"
            ? item.name || item.title || item.description || ""
            : item}
        </span>
      ))}
    </div>
  </>
)}
            </div>

            <div className="holiday-help-card">
  <PhoneCall size={34} />
  <div>
    <h3>Need Help?</h3>
    <p>Call us : 9355471999</p>
    <p>Mail us : Holidays@makebeetrip.com</p>
    <p>Website : makebeetrip.com</p>
  </div>
</div>
          </aside>
        </div>
      </section>

      <button
  className="holiday-enquire-float"
  type="button"
  onClick={() => navigate("/contact")}
>
  <MessageSquareText size={20} />
  <span>Enquire<br />Now</span>
</button>

      <button className="holiday-back-floating" type="button" onClick={() => navigate(`/holidays/packages/${placeSlug}`)}>
        <ArrowLeft size={16} />
        Back to Packages
        <ArrowRight size={16} />
      </button>

      {showCalendar && (
        <div className="holiday-calendar-overlay" onClick={(event) => event.target === event.currentTarget && setShowCalendar(false)}>
          <section className="holiday-calendar-modal" role="dialog" aria-modal="true" aria-label="Select travel date">
            <button className="holiday-calendar-close" type="button" onClick={() => setShowCalendar(false)}>×</button>
            <p>Choose Travel Date</p>
            <h2>{pkg.title}</h2>
            <span>
  {pkg.duration}
  {pkg.group_size ? ` | ${pkg.group_size} Travellers` : ""}
</span>
            <div className="holiday-date-field">
              <label htmlFor="holiday-departure" className="holiday-field-label">DEPARTURE</label>
              <input
  id="holiday-departure"
  type="date"
  value={selectedTravelDate}
  min={(() => {
    const today = new Date();
    today.setDate(
      today.getDate() + Number(pkg.booking_min_advance_days || 0)
    );

    return today.toISOString().split("T")[0];
  })()}
  max={(() => {
    const today = new Date();
    today.setFullYear(
      today.getFullYear() + Number(pkg.booking_max_advance_years || 2)
    );

    return today.toISOString().split("T")[0];
  })()}
  onChange={(e) => {
    const value = e.target.value;

    if (!isValidDepartureDate(value)) {
      setTravelDateError(
        "Selected date is not an available departure date for this package."
      );
      setSelectedTravelDate("");
      return;
    }

    setTravelDateError("");
    setSelectedTravelDate(value);
  }}
  className="holiday-date-input"
/>
              {travelDateError && (
                <p role="alert" style={{ color: "#e55a4e", fontSize: ".8rem", marginTop: 6 }}>
                  {travelDateError}
                </p>
              )}
            </div>
            <div className="holiday-calendar-actions">
              <button type="button" onClick={() => setShowCalendar(false)}>Cancel</button>
              <button
  type="button"
  disabled={!selectedTravelDate}
  onClick={() => {
    if (!selectedTravelDate) return;

    if (!isValidDepartureDate(selectedTravelDate)) {
      alert("Please select a valid available departure date.");
      return;
    }

    setShowCalendar(false);

    navigate(`/holidays/confirm/${placeSlug}/${packageSlug}`, {
      state: {
        packageId: pkg.id,
        from: location.state?.from || "New Delhi",
        destination:
          pkg.destination ||
          pkg.place ||
          pkg.location ||
          destination,
        travelDate: selectedTravelDate,
        adults: 2,
  children: 0,
  infants: 0,
        pricingBasis: pkg.pricing_basis || "per_person",
      },
    });
  }}
>
  Continue <ArrowRight size={15} />
</button>
            </div>
          </section>
        </div>
      )}

      
    </main>
  );
}
